// Recrutement — constantes partagées client/serveur (phase 2, pipeline).
// Port de design/recrutement rec-data.jsx (REC_STAGES/REC_MOTIFS) et
// rec2-emails.jsx (modèles d'emails candidat par étape).
/** Étapes du pipeline (§6.2) — l'ordre est celui du funnel. REJETE et
 *  VIVIER sont des sorties possibles à chaque étape, pas des colonnes. */
export const REC_STAGES = [
    { id: 'NOUVELLE', lbl: 'Nouvelle', c: '#74747e' },
    { id: 'PRESEL', lbl: 'Présélection CV', c: '#0792a8' },
    { id: 'APPEL', lbl: 'Appel de qualif', c: '#2a6fdb' },
    { id: 'TEST', lbl: 'Cas pratique', c: '#0bb5ab' },
    { id: 'ENTRETIEN', lbl: 'Entretien', c: '#7037ef' },
    { id: 'FINALISTE', lbl: 'Finaliste', c: '#c41d8f' },
    { id: 'OFFRE', lbl: 'Offre', c: '#d8590f' },
    { id: 'RECRUTE', lbl: 'Recruté', c: '#0a9d72' },
];
export const REC_EXITS = [
    { id: 'REJETE', lbl: 'Rejeté', c: '#e23b3b' },
    { id: 'VIVIER', lbl: 'Vivier', c: '#74747e' },
];
/** Motifs de rejet — liste fermée (alimente les analytics, spec §6.2). */
export const REC_MOTIFS = [
    'Profil inadéquat',
    'Expérience insuffisante',
    'Prétentions hors budget',
    'Disponibilité',
    'Échec cas pratique',
    'Échec entretien technique',
    'Échec entretien manager',
    'Incohérence à la vérification',
    'Meilleur candidat retenu',
    'Sans réponse',
    'Désistement',
];
/** Sources de candidature (Candidate.source, spec §5). */
export const REC_SOURCES = ['annonce', 'spontanée', 'référé', 'chasse'];
export const REC_EMAIL_TEMPLATES = [
    {
        id: 'ack', lbl: 'Accusé de réception', step: 'Nouvelle', stages: ['NOUVELLE'],
        subj: 'Votre candidature — {{poste}}',
        body: [
            'Bonjour {{prenom}},',
            'Nous avons bien reçu votre candidature au poste de {{poste}} chez {{entite}}. Votre dossier est en cours d’examen — nous revenons vers vous sous 10 jours ouvrés.',
            'Merci de la confiance que vous nous accordez.',
        ],
    },
    {
        id: 'call', lbl: 'Invitation — appel de qualification', step: 'Appel', stages: ['APPEL', 'PRESEL'],
        subj: 'Premier échange — {{poste}}',
        body: [
            'Bonjour {{prenom}},',
            'Votre profil a retenu notre attention pour le poste de {{poste}}. Nous vous proposons un premier échange téléphonique de 20 minutes.',
            'Répondez à cet email avec vos disponibilités cette semaine — nous nous adaptons.',
        ],
    },
    {
        id: 'itw', lbl: 'Convocation à un entretien', step: 'Entretien', stages: ['ENTRETIEN'],
        subj: 'Entretien — {{poste}} chez {{entite}}',
        body: [
            'Bonjour {{prenom}},',
            'Suite à notre échange, nous souhaitons vous rencontrer pour un entretien concernant le poste de {{poste}}.',
            'Nous revenons vers vous très vite avec la date, l’heure et la modalité. Comptez environ une heure.',
        ],
    },
    {
        id: 'itwthx', lbl: 'Merci — suite d’entretien', step: 'Entretien', stages: ['ENTRETIEN', 'FINALISTE'],
        subj: 'Merci pour votre temps, {{prenom}}',
        body: [
            'Bonjour {{prenom}},',
            'Merci pour l’entretien concernant le poste de {{poste}}. L’équipe finalise son retour — réponse sous 5 jours ouvrés.',
            'N’hésitez pas si vous avez des questions d’ici là.',
        ],
    },
    {
        id: 'nok', lbl: 'Réponse négative', step: 'Toutes étapes',
        stages: ['NOUVELLE', 'PRESEL', 'APPEL', 'TEST', 'ENTRETIEN', 'FINALISTE', 'OFFRE', 'REJETE'],
        subj: 'Votre candidature — {{poste}}',
        body: [
            'Bonjour {{prenom}},',
            'Merci pour le temps consacré à votre candidature au poste de {{poste}}. Nous ne donnerons pas suite pour cette ouverture.',
            'Avec votre accord, nous conservons votre dossier 24 mois pour de futures opportunités (loi 18-07).',
        ],
    },
];
/** Rendu des variables d'un modèle. */
export function recFillTemplate(text, vars) {
    return text.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? k);
}
// ─── phase 3 — évaluation (appel, entretiens, scorecards) ────
/** Verdicts de clôture d'un appel de qualification. `go` avance le
 *  candidat à l'entretien, `vivier` le sort au vivier, `stop` ouvre le
 *  rejet à motif obligatoire. */
export const REC_CALL_VERDICTS = ['go', 'vivier', 'stop'];
/** Verdicts de scorecard — obligatoires, même convention que le proto. */
export const REC_SC_VERDICTS = [
    { id: 'oui', lbl: 'Oui — recruter' },
    { id: 'reserve', lbl: 'Oui, avec réserve' },
    { id: 'non', lbl: 'Non' },
];
/** Types d'entretien proposés par défaut — complétés par les clés des
 *  kits du rôle (templates.kits), qui portent les questions. */
export const REC_ITW_TYPES = [
    'Entretien RH',
    'Entretien technique',
    'Entretien manager',
    'Entretien final',
    'Défense du cas pratique',
    'Journée de travail',
];
export const REC_ITW_MODES = ['présentiel', 'visio'];
export const REC_ITW_STATUS = [
    { id: 'PLANIFIE', lbl: 'Planifié' },
    { id: 'REALISE', lbl: 'Réalisé' },
    { id: 'ANNULE', lbl: 'Annulé' },
    { id: 'NO_SHOW', lbl: 'No-show' },
];
// ─── phase 4 — offres ────────────────────────────────────────
/** Statuts d'une version d'offre (§6.3) — couleurs du proto rec8. Un
 *  refus interne renvoie la version en BROUILLON (jamais terminal); une
 *  contre-proposition fige la version en CONTRE et la suivante prend le
 *  relais en re-entrant la chaîne de validation. */
export const REC_OFFER_STATUS = [
    { id: 'BROUILLON', lbl: 'Brouillon', c: '#74747e' },
    { id: 'EN_ATTENTE', lbl: 'En validation', c: '#d8590f' },
    { id: 'VALIDEE', lbl: 'Validée — à envoyer', c: '#7037ef' },
    { id: 'ENVOYEE', lbl: 'Envoyée', c: '#0792a8' },
    { id: 'ACCEPTEE', lbl: 'Acceptée', c: '#0a9d72' },
    { id: 'REFUSEE', lbl: 'Refusée', c: '#e23b3b' },
    { id: 'CONTRE', lbl: 'Contre-proposition', c: '#2a6fdb' },
];
/** Avantages proposés par le constructeur (proto RecBuilder). */
export const REC_OFFER_AVANTAGES = [
    'Mutuelle famille',
    'Télétravail 2 j/sem',
    'Télétravail 3 j/sem',
    'Prime de transport',
    'Formation annuelle',
];
export const REC_OFFER_ESSAI = ['3 mois', '6 mois', '6 mois renouvelable'];
/** Validité de l'offre après envoi, en jours. */
export const REC_OFFER_DEADLINES = [7, 10, 14];
/** Réponses candidat possibles à une offre envoyée. */
export const REC_OFFER_RESPONSES = ['acceptee', 'refusee', 'contre'];
// ─── Onboarding (phase 5, rec9-onboard.jsx) ─────────────────
/** Horizons du plan d'intégration — due = « avant J+due ». */
export const REC_OB_HORIZONS = [
    { id: 'J1', lbl: 'Jour 1', due: 1, c: '#7037ef' },
    { id: 'S1', lbl: 'Semaine 1', due: 7, c: '#0792a8' },
    { id: 'M1', lbl: 'Mois 1', due: 30, c: '#2a6fdb' },
    { id: 'J90', lbl: '90 jours', due: 90, c: '#0a9d72' },
];
/** Phase du parcours selon J−/J+ (couleur d'accent des cartes). */
export const REC_OB_PHASES = {
    pre: { lbl: 'Pré-boarding', c: '#2a6fdb', ic: '🧳' },
    j1: { lbl: 'Premiers jours', c: '#7037ef', ic: '🚀' },
    m1: { lbl: 'Montée en charge', c: '#0792a8', ic: '📈' },
    j90: { lbl: 'Fin d’essai', c: '#0a9d72', ic: '🎓' },
};
/** Responsables des tâches du plan (templates du catalogue). */
export const REC_OB_OWNERS = {
    Manager: '#7037ef',
    RH: '#0a9d72',
    IT: '#2a6fdb',
};
/** États d'une pièce du dossier admin. */
export const REC_OB_DOC_STATES = ['manq', 'recu', 'ok'];
/** Durée de période d'essai (mois) depuis le champ essai de l'offre —
 *  alimente Employee.probationMonths au handoff. */
export function recEssaiMonths(essai) {
    return essai.startsWith('3') ? 3 : 6;
}
/** « 6 mois renouvelable » → la période d'essai est renouvelable une fois. */
export function recEssaiRenewable(essai) {
    return /renouvelable/i.test(essai);
}
/** Note pondérée d'une scorecard sur la grille du rôle (échelle 1–5,
 *  coefficients du catalogue) — arrondie à une décimale. */
export function recWeightedScore(scores, grid) {
    const rated = grid.filter((g) => typeof scores[g.id] === 'number');
    if (!rated.length)
        return null;
    const tot = rated.reduce((s, g) => s + g.co, 0);
    const sum = rated.reduce((s, g) => s + scores[g.id] * g.co, 0);
    return tot > 0 ? Math.round((sum / tot) * 10) / 10 : null;
}
// ═══ v2 — preuves avant l'embauche, barre, essai ═════════════
/** Les 4 dimensions notées séparément (chaque critère de grille en
 *  porte une, obligatoirement). */
export const REC_DIMS = [
    { id: 'PERFORMANCE', lbl: 'Performance', short: 'Perf.', c: '#7037ef', hint: 'pousse à travers les obstacles pour livrer un résultat' },
    { id: 'CONNAISSANCES', lbl: 'Connaissances', short: 'Conn.', c: '#0792a8', hint: 'savoir-faire du métier, outils, méthode' },
    { id: 'PERSONNALITE', lbl: 'Personnalité', short: 'Pers.', c: '#c41d8f', hint: 'critique, pression, relation à l’équipe' },
    { id: 'ALIGNEMENT', lbl: 'Alignement', short: 'Align.', c: '#0a9d72', hint: 'nos façons de travailler, projet à 2 ans' },
];
export const REC_DIM_IDS = REC_DIMS.map((d) => d.id);
export const REC_SETTINGS_DEFAULT = {
    barOverall: 3.8, barPerf: 4.0, barMin: 3.0, testPass: 3.0, testFee: 15000,
    cp3: [15, 30, 45, 60, 75], cp6: [15, 30, 60, 90, 120], cp6FinalBefore: 14,
};
/** Score pondéré global + par dimension. */
export function recDimScore(grid, scores) {
    let t = 0;
    let c = 0;
    const acc = Object.fromEntries(REC_DIM_IDS.map((d) => [d, { t: 0, c: 0 }]));
    for (const g of grid) {
        const v = scores[g.id];
        if (typeof v !== 'number')
            continue;
        t += v * g.co;
        c += g.co;
        if (g.dim) {
            acc[g.dim].t += v * g.co;
            acc[g.dim].c += g.co;
        }
    }
    const dims = Object.fromEntries(REC_DIM_IDS.map((d) => [d, acc[d].c ? acc[d].t / acc[d].c : null]));
    return { overall: c ? t / c : null, dims, complete: grid.length > 0 && grid.every((g) => typeof scores[g.id] === 'number') };
}
/** Moyenne critère par critère de plusieurs scorecards. */
export function recAvgScores(list) {
    const sum = {};
    const n = {};
    for (const s of list)
        for (const [k, v] of Object.entries(s)) {
            sum[k] = (sum[k] ?? 0) + v;
            n[k] = (n[k] ?? 0) + 1;
        }
    return Object.fromEntries(Object.keys(sum).map((k) => [k, sum[k] / n[k]]));
}
const f1 = (v) => (v == null ? '—' : (Math.round(v * 10) / 10).toFixed(1).replace('.', ','));
/** Verdict de barre : global ≥ X, Performance ≥ Y, aucune dimension < Z. */
export function recBar(s, st) {
    if (!s.complete || s.overall == null)
        return { state: 'pend', fails: [] };
    const f = [];
    if (s.overall < st.barOverall)
        f.push(`Global ${f1(s.overall)} < ${f1(st.barOverall)}`);
    const p = s.dims.PERFORMANCE;
    const perfFail = p != null && p < st.barPerf;
    if (perfFail)
        f.push(`Performance ${f1(p)} < ${f1(st.barPerf)}`);
    for (const d of REC_DIMS) {
        if (d.id === 'PERFORMANCE' && perfFail)
            continue;
        const v = s.dims[d.id];
        if (v != null && v < st.barMin)
            f.push(`${d.lbl} ${f1(v)} < ${f1(st.barMin)}`);
    }
    return { state: f.length ? 'ko' : 'ok', fails: f };
}
/** Une note de 1, 2 ou 5 exige une preuve écrite. */
export const recNeedsEvidence = (v) => v === 1 || v === 2 || v === 5;
/** Une grille est « prête » quand les 4 dimensions sont couvertes. */
export function recGridCovered(grid) {
    return REC_DIM_IDS.every((d) => grid.some((g) => g.dim === d));
}
/** Questions de référence (6, fixes). La 5ᵉ est un choix fermé. */
export const REC_REF_QUESTIONS = [
    'Dans quel contexte avez-vous travaillé ensemble ?',
    'Ses points forts, avec un exemple concret ?',
    'Que lui faudrait-il améliorer pour être à 10/10 ?',
    'Comment réagit-il·elle à la critique ou à la pression ?',
    'Le·la réembaucheriez-vous ?',
    'Autre chose que je devrais savoir ?',
];
export const REC_REF_RELATIONS = ['MANAGER', 'PAIR', 'AUTRE'];
export const REC_REHIRE = ['OUI', 'HESITATION', 'NON'];
/** Porte « vérification » : ≥ 2 références dont ≥ 1 manager, certificats
 *  vérifiés, intégrité OK. */
export function recVerifGate(v) {
    const refs = v?.refs ?? [];
    const mgr = refs.filter((r) => r.relation === 'MANAGER').length;
    const refsOk = refs.length >= 2 && mgr >= 1;
    const cert = v?.cert === true;
    const integ = v?.integrity === 'OK';
    return { refsOk, mgr, n: refs.length, cert, integ, problem: v?.integrity === 'PROBLEME', ok: refsOk && cert && integ, done: [refsOk, cert, integ].filter(Boolean).length };
}
export function recCpPlan(months, st) {
    if (months <= 3) {
        const [a, r1, p, r2, fin] = st.cp3;
        return [
            { key: 'alert', day: a ?? 15, type: 'alert', lbl: 'Alerte précoce' },
            { key: 'r1', day: r1 ?? 30, type: 'review', lbl: `Revue J+${r1 ?? 30}` },
            { key: 'pulse', day: p ?? 45, type: 'pulse', lbl: 'Pouls équipe' },
            { key: 'r2', day: r2 ?? 60, type: 'review', lbl: `Revue J+${r2 ?? 60}` },
            { key: 'final', day: fin ?? 75, type: 'final', lbl: 'Décision finale' },
        ];
    }
    const [a, r1, r2, p, r3] = st.cp6;
    const end = months * 30;
    return [
        { key: 'alert', day: a ?? 15, type: 'alert', lbl: 'Alerte précoce' },
        { key: 'r1', day: r1 ?? 30, type: 'review', lbl: `Revue J+${r1 ?? 30}` },
        { key: 'r2', day: r2 ?? 60, type: 'review', lbl: `Revue J+${r2 ?? 60}` },
        { key: 'pulse', day: p ?? 90, type: 'pulse', lbl: 'Pouls équipe' },
        { key: 'r3', day: r3 ?? 120, type: 'review', lbl: `Revue J+${r3 ?? 120}` },
        { key: 'final', day: end - st.cp6FinalBefore, type: 'final', lbl: 'Décision finale' },
    ];
}
export const REC_PULSE_QUESTIONS = [
    'Qu’apporte-t-il·elle à l’équipe ?',
    'Qu’est-ce qui coince au quotidien ?',
    'À votre place, le·la garderiez-vous ?',
];
/** Statut d'un objectif des 90 premiers jours. */
export const REC_OUTCOME_ST = [
    { id: 'TODO', lbl: 'À démarrer' },
    { id: 'ON', lbl: 'En bonne voie' },
    { id: 'OK', lbl: 'Atteint' },
    { id: 'LATE', lbl: 'En retard' },
];
//# sourceMappingURL=recrutement.js.map